import React, { useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';

import { Alert, Button, Card, Form, Input, Typography } from 'antd';

import { useField } from 'formik';
import type { FieldProps } from 'formik';
import { Hint } from 'v2/components/Shared/Hint';
import useMask from 'v2/hooks/useMask';
import type { TestResult } from 'v2/interfaces/webhook2';
import webhookService from 'v2/services/webhook2';

import { Icon } from '@eduzz/ui-antd-theme';

type UrlValidationFormFieldProps = FieldProps & {
  mask?: string;
  variant?: 'outlined' | 'borderless' | 'filled';
  label?: string;
  helperText?: React.ReactNode;
  afterValidate?: React.ReactNode;
  onValidateUrl: (valid: boolean, url?: string) => void;
  resetValidation?: number;
  helpLink?: string;
  validateUrlCallback?: (url: string, type: string, contentType: string) => Promise<TestResult>;
  placeholder?: string;
  disabled?: boolean;
};

const UrlValidationFormField = ({
  field,
  form,
  helperText,
  afterValidate,
  label,
  helpLink = 'https://eduzz.github.io/webhook-v2',
  validateUrlCallback = webhookService.validateUrl,
  resetValidation,
  mask,
  onValidateUrl,
  variant: _variant,
  ...props
}: UrlValidationFormFieldProps) => {
  const name = field.name;
  const [, meta] = useField(name);

  const [result, setResult] = React.useState<TestResult>();
  const [validating, setValidating] = React.useState(false);
  const value = field?.value || '';
  const { maskedValue, maskClean } = useMask(mask, value);

  const onChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setResult(null);
      onValidateUrl(false);
      return form.setFieldValue(field.name, maskClean(e.target.value));
    },
    [onValidateUrl, form, field.name, maskClean]
  );

  const onBlurHandler = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const v = e.target.value;

      form.setFieldTouched(field.name, true);

      if (!v) return;

      form.setFieldValue(field.name, maskClean(e.target.value));
    },
    [form, field.name, maskClean]
  );

  const validUrl = useCallback(async () => {
    try {
      setValidating(true);
      const values = form.values;
      const result = await validateUrlCallback(values.url, values.type, values.content_type);
      setValidating(false);
      setResult(result);
      onValidateUrl(result.valid, values.url);
    } catch {
      toast.error('Ocorreu um erro ao validar a url');
      setValidating(false);
    }
  }, [onValidateUrl, validateUrlCallback, form]);

  const errorText = meta.touched && (meta.error || null);
  const showResult = !!result?.requestData;
  const urlSuccess = showResult && result?.valid;
  const urlFailed = showResult && !result?.valid;
  const isUrlFormatValid = !errorText;

  useEffect(() => {
    if (resetValidation) {
      setResult(null);
      onValidateUrl(false);
    }
  }, [resetValidation, onValidateUrl]);

  return (
    <>
      <Hint inputName={field.name}>
        <Form.Item
          layout='vertical'
          className='mb-0!'
          label={label}
          validateStatus={errorText ? 'error' : undefined}
        >
          <div className='flex flex-col gap-2'>
            <Input
              className='w-full'
              status={errorText ? 'error' : undefined}
              name={name}
              value={maskedValue}
              onChange={onChangeHandler}
              onBlur={onBlurHandler}
              {...props}
            />

            {(errorText || helperText) && (
              <Typography.Text type={errorText ? 'danger' : 'secondary'} className='text-sm'>
                {errorText || helperText}
              </Typography.Text>
            )}

            <div>
              <Button
                type='primary'
                onClick={validUrl}
                icon={<Icon.Zap size={16} />}
                loading={validating}
                disabled={!isUrlFormatValid || !field.value || validating}
              >
                Validar URL
              </Button>
            </div>
            {afterValidate}
          </div>
        </Form.Item>
      </Hint>

      {showResult && (
        <div className='flex flex-col gap-3 pt-2'>
          {urlSuccess && <Alert type='success' showIcon message='URL verificada com sucesso!' />}

          {urlFailed && (
            <Alert
              type='error'
              showIcon
              message={(
                <Typography.Text>
                  Tentativa de envio teste para url falhou!
                </Typography.Text>
              )}
              description={(
                <Typography.Text type='secondary' className='text-sm'>
                  Verifique os detalhes abaixo.
                </Typography.Text>
              )}
              action={
                <Typography.Link href={helpLink} target='_blank' rel='noreferrer'>
                  Ajuda
                </Typography.Link>
              }
            />
          )}

          <div className='flex flex-col gap-1'>
            <Typography.Text strong>Dados enviados para o servidor (URL)</Typography.Text>
            <Card size='small' className='overflow-auto wrap-break-word'>
              <Typography.Paragraph className='mb-0!'>
                <Typography.Text strong>POST</Typography.Text>
                <br />
                <Typography.Text>{value}</Typography.Text>
              </Typography.Paragraph>
            </Card>
          </div>

          <div className='flex flex-col gap-1'>
            <Typography.Text strong>Resposta do servidor (URL)</Typography.Text>
            <Card size='small' className='overflow-auto wrap-break-word'>
              <Typography.Link
                target='_blank'
                href={`https://developer.mozilla.org/pt-BR/docs/Web/HTTP/Status/${result?.response.status}`}
                rel='noreferrer'
                className='mb-2 inline-flex items-center gap-1'
              >
                <Icon.Globe size={16} />
                Status: {result?.response.status} {result?.response.statusText}
              </Typography.Link>
              <Typography.Paragraph className='mb-1!'>{result?.response.helperText}</Typography.Paragraph>
              <Typography.Paragraph className='mb-0!'>
                <Typography.Text strong>Tempo de resposta:</Typography.Text> {`${result?.response.duration || 0} ms`}
              </Typography.Paragraph>
            </Card>
          </div>

          {!urlSuccess && (
            <div className='flex flex-col gap-1'>
              <Typography.Text strong>Corpo da resposta</Typography.Text>
              <Card size='small' className='overflow-auto wrap-break-word'>
                <Typography.Text code>{result?.response?.data ?? '-'}</Typography.Text>
              </Card>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default UrlValidationFormField;
